import { api } from './index'
import type {
  OloWebhookCredential,
  OloWebhookCredentialResponseWire,
} from './oloWebhookCredential.types'
import { isErrorWithMessages } from './utils'

// Fail-soft wire→domain transform (mirrors liveUpdates.ts's transform*
// convention): a missing/malformed envelope coerces to the safe "not
// configured, no URL" default rather than throwing.
export const transformOloWebhookCredential = (
  response: OloWebhookCredentialResponseWire
): OloWebhookCredential => {
  const credential = response?.olo_webhook_credential
  return {
    webhookUrl: credential?.webhook_url ?? '',
    configured: credential?.configured === true,
  }
}

// The PUT's 400 envelope carries a single validation message (ownership
// conflict, blank secret, etc) under errors.messages — surfaces it verbatim,
// falling back to null (generic copy) for anything else (network failure,
// 5xx).
export const toOloWebhookCredentialSaveError = (
  error: unknown
): string | null => {
  if (!isErrorWithMessages(error)) return null
  return error.data.errors.messages[0] ?? null
}

export const oloWebhookCredentialApi = api.injectEndpoints({
  endpoints: builder => ({
    getOloWebhookCredential: builder.query<OloWebhookCredential, void>({
      query: () => 'olo_webhook_credentials',
      transformResponse: transformOloWebhookCredential,
      providesTags: ['OloWebhookCredential'],
    }),

    updateOloWebhookCredential: builder.mutation<OloWebhookCredential, string>({
      query: sharedSecret => ({
        url: 'olo_webhook_credentials',
        method: 'PUT',
        body: { shared_secret: sharedSecret },
      }),
      transformResponse: transformOloWebhookCredential,
      invalidatesTags: (result, error) =>
        error ? [] : ['OloWebhookCredential'],
    }),
  }),
})

export const {
  useGetOloWebhookCredentialQuery,
  useUpdateOloWebhookCredentialMutation,
} = oloWebhookCredentialApi
